import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { concat } from '@ember/helper';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import Callout from 'tio-ui/components/callout';
import {
  PhPiggyBank,
  PhTrendUp,
  PhCurrencyDollar,
  PhGraduationCap,
  PhChartLine,
  PhLightbulb,
  PhArrowRight,
  PhBank,
  PhPercent,
  PhCalendar,
  PhSparkle,
} from 'ember-phosphor-icons';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import PageHero from 'tio-ui/components/page-hero';
import StatCard from 'tio-ui/components/stat-card';
import ProgressBar from 'tio-ui/components/progress-bar';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type {
  EmployerContributions,
  FinancialSnapshot,
  Section127Recommendation,
} from 'tio-employee/services/career-pathways-api';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import type { LiveSection127Info } from 'tio-employee/services/reimagined-data';

interface FinanceModel {
  snapshot: FinancialSnapshot;
  recommendation: Section127Recommendation;
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  loansData: {
    loans: LoanAccount[];
    hasLinkedLoans: boolean;
    isDemoMode: boolean;
  };
  section127Info: LiveSection127Info;
}

interface FinanceIndexSignature {
  Args: {
    model: FinanceModel;
  };
}

export default class CareerPathwaysFinanceIndex extends Component<FinanceIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get employerName() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get snapshot() {
    return this.args.model.snapshot;
  }

  get recommendation() {
    return this.args.model.recommendation;
  }

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  get section127() {
    return this.snapshot.section127;
  }

  get loans() {
    return this.snapshot.studentLoans;
  }

  /**
   * Use live employer contribution data if available, otherwise fall back to snapshot
   */
  get contributions() {
    if (this.args.model.employerContributions.isLiveData) {
      return this.args.model.employerContributions.contributions;
    }
    return this.snapshot.employerContributions;
  }

  get optimization() {
    return this.snapshot.optimizationPotential;
  }

  /**
   * Data source helpers for the DataIndicator component
   */
  get studentLoansSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.employerContributions === 'live' ? 'live' : 'placeholder';
  }

  get section127Source(): DataSource {
    // Use live data if Section 127 info was fetched from real plan/contribution data
    if (this.isDemoMode) return 'demo';
    return this.args.model.section127Info.isLiveData ? 'live' : 'placeholder';
  }

  get optimizationSource(): DataSource {
    // AI optimization is always placeholder until fully built
    if (this.isDemoMode) return 'demo';
    return 'placeholder';
  }

  get utilizationPercent() {
    return Math.round((this.section127.yearToDateUsed / this.section127.annualLimit) * 100);
  }

  get loanAllocationPercent() {
    const total = this.section127.loanAllocation + this.section127.tuitionAllocation;
    return total > 0 ? Math.round((this.section127.loanAllocation / total) * 100) : 50;
  }

  get tuitionAllocationPercent() {
    return 100 - this.loanAllocationPercent;
  }

  get formattedPayoffDate() {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(new Date(this.loans.estimatedPayoffDate));
  }

  get strategySummary() {
    const strategy = this.section127.strategy;
    if (strategy === 'loan-focused') {
      return 'Prioritizing student loan payments to reduce debt faster';
    }
    if (strategy === 'tuition-focused') {
      return 'Maximizing education investment for career growth';
    }
    return 'Balanced approach between debt reduction and career development';
  }

  get benefitSubtitle() {
    return `Maximize your tax-free ${formatCurrency(
      this.section127.annualLimit
    )} education benefit. Our AI analyzes your loans, career goals, and financial situation to optimize your allocation.`;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Hero Banner - Section 127 overview (placeholder until we have real allocation API) }}
      <DataIndicator
        @source={{this.section127Source}}
        @showBadge={{true}}
        @dataSourceId="section-127"
        class="mb-6"
      >
        <PageHero
          @icon={{PhPiggyBank}}
          @title="Section 127 Optimizer"
          @subtitle={{this.benefitSubtitle}}
          @gradient="cool"
          role="banner"
        >
          <:stat>
            <div class="tio-text-4xl-bold">{{formatCurrency
                this.optimization.potentialSavings
              }}</div>
            <Text class="tio-finance-hero-caption">Potential Annual Savings</Text>
          </:stat>
        </PageHero>
      </DataIndicator>

      {{! Key Metrics Row - Section 127 stats (placeholder until we have real allocation API) }}
      <DataIndicator
        @source={{this.section127Source}}
        @showBadge={{true}}
        @dataSourceId="section-127"
      >
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            @icon={{PhCurrencyDollar}}
            @label="Annual Benefit"
            @value={{formatCurrency this.section127.annualLimit}}
            @subtext="Tax-free under Section 127"
            @color="emerald"
          />

          <StatCard
            @icon={{PhChartLine}}
            @label="Used This Year"
            @value={{formatCurrency this.section127.yearToDateUsed}}
            @color="blue"
          >
            <:extra>
              <ProgressBar @value={{this.utilizationPercent}} @color="blue" />
              <Text @variant="muted" @size="xs" class="mt-1">{{this.utilizationPercent}}% utilized</Text>
            </:extra>
          </StatCard>

          <StatCard
            @icon={{PhBank}}
            @label="Remaining"
            @value={{formatCurrency this.section127.remaining}}
            @subtext="Available to allocate"
            @color="violet"
            @valueClass="tio-fg-success"
          />

          <StatCard
            @icon={{PhPercent}}
            @label="Tax Savings YTD"
            @value={{formatCurrency this.optimization.taxSavingsYtd}}
            @subtext="Based on 25% marginal rate"
            @color="amber"
          />
        </div>
      </DataIndicator>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Current Allocation }}
        <DataIndicator
          @source={{this.section127Source}}
          @showBadge={{true}}
          @dataSourceId="section-127"
          class="lg:col-span-2"
        >
          <div class="tio-bg-surface rounded-lg p-6 tio-hairline-card">
            <div class="tio-row-between mb-4">
              <Heading @level={{2}} @size="lg">Current Allocation</Heading>
              <Chip @intent={{if (eq this.section127.strategy "balanced") "primary" "default"}}>
                {{if
                  (eq this.section127.strategy "loan-focused")
                  "Loan Focused"
                  (if (eq this.section127.strategy "tuition-focused") "Tuition Focused" "Balanced")
                }}
              </Chip>
            </div>

            <Text @variant="muted" class="mb-6">{{this.strategySummary}}</Text>

            {{! Allocation Visualization }}
            <div class="mb-6">
              <div class="flex h-8 rounded-lg overflow-hidden">
                <div
                  class="tio-gradient-info tio-row-center tio-fg-inverse tio-text-sm-medium"
                  style={{htmlSafe (concat "width: " this.loanAllocationPercent "%")}}
                >
                  {{this.loanAllocationPercent}}% Loans
                </div>
                <div
                  class="tio-gradient-brand tio-row-center tio-fg-inverse tio-text-sm-medium"
                  style={{htmlSafe (concat "width: " this.tuitionAllocationPercent "%")}}
                >
                  {{this.tuitionAllocationPercent}}% Tuition
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              {{! Loan Allocation Card }}
              <div class="tio-bg-info-soft rounded-lg p-4 border tio-border-info">
                <div class="tio-row-tight mb-2">
                  <PhBank @size={{20}} class="tio-fg-info" />
                  <Text @weight="medium" class="tio-fg-info-h">Student Loan Payments</Text>
                </div>
                <div class="tio-text-2xl-bold tio-fg-info">{{formatCurrency
                    this.section127.loanAllocation
                  }}</div>
                <Text @variant="muted" @size="sm" class="mt-1">Allocated this year</Text>
              </div>

              {{! Tuition Allocation Card }}
              <div class="tio-bg-brand-wash rounded-lg p-4 border tio-border-brand-soft">
                <div class="tio-row-tight mb-2">
                  <PhGraduationCap @size={{20}} class="tio-fg-brand" />
                  <Text @weight="medium" class="tio-fg-brand">Tuition Assistance</Text>
                </div>
                <div class="tio-text-2xl-bold tio-fg-brand">
                  {{formatCurrency this.section127.tuitionAllocation}}
                </div>
                <Text @variant="muted" @size="sm" class="mt-1">Allocated this year</Text>
              </div>
            </div>

            <div class="mt-6 pt-4 border-t tio-border-default">
              <LinkTo
                @route="authenticated.reimagined.finance.optimizer"
                class="tio-inline-row gap-2 tio-fg-success hover:tio-fg-success-body font-medium"
              >
                <PhSparkle @size={{20}} />
                <span>Use AI Optimizer to adjust allocation</span>
                <PhArrowRight @size={{16}} />
              </LinkTo>
            </div>
          </div>
        </DataIndicator>

        {{! Student Loan Summary }}
        <DataIndicator
          @source={{this.studentLoansSource}}
          @showBadge={{true}}
          @dataSourceId="student-loans"
        >
          <div class="tio-bg-surface rounded-lg p-6 tio-hairline-card">
            <div class="tio-row-tight mb-4">
              <PhBank @size={{24}} class="tio-fg-secondary" />
              <Heading @level={{2}} @size="lg">Student Loan Summary</Heading>
            </div>

            <div class="space-y-4">
              <div>
                <Text @variant="muted" @size="sm">Total Balance</Text>
                <div class="tio-text-2xl-bold tio-fg-primary">{{formatCurrency
                    this.loans.totalBalance
                  }}</div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <Text @variant="muted" @size="sm">Monthly Payment</Text>
                  <div class="tio-text-lg-semibold tio-fg-primary">{{formatCurrency
                      this.loans.monthlyPayment
                    }}</div>
                </div>
                <div>
                  <Text @variant="muted" @size="sm">Interest Rate</Text>
                  <div
                    class="tio-text-lg-semibold tio-fg-primary"
                  >{{this.loans.interestRate}}%</div>
                </div>
              </div>

              <div>
                <Text @variant="muted" @size="sm">Servicer</Text>
                <div class="tio-text-lg-medium tio-fg-primary">{{this.loans.servicer}}</div>
              </div>

              <div class="pt-4 border-t tio-border-default">
                <div class="tio-row-tight tio-fg-success mb-1">
                  <PhCalendar @size={{20}} />
                  <Text @weight="medium">Estimated Payoff</Text>
                </div>
                <div class="tio-text-xl-bold tio-fg-primary">{{this.formattedPayoffDate}}</div>
                <Text @variant="muted" @size="sm">
                  Can accelerate by
                  {{this.optimization.projectedPayoffAcceleration}}
                  with optimization
                </Text>
              </div>
            </div>
          </div>
        </DataIndicator>
      </div>

      {{! AI Recommendation Banner }}
      <DataIndicator
        @source={{this.optimizationSource}}
        @showBadge={{true}}
        @dataSourceId="ai-recommendations"
        class="mt-6"
      >
        <Callout @intent="warning" @gradient={{true}} @title="AI Recommendation">
          <:icon><PhLightbulb @size={{28}} /></:icon>
          <:default>
            <p class="mb-4">{{this.recommendation.reasoning}}</p>

            <div class="tio-wrap-tight">
              {{#each this.recommendation.factors as |factor|}}
                <Chip @intent={{if (eq factor.impact "high") "warning" "default"}}>
                  {{factor.name}}:
                  {{factor.impact}}
                  impact
                </Chip>
              {{/each}}
            </div>
          </:default>
          <:action>
            <Button
              @route="authenticated.reimagined.finance.optimizer"
              @intent="primary"
              @appearance="outlined"
              @icon={{PhArrowRight}}
              @iconPlacement="end"
              @label="View Full Analysis"
            />
          </:action>
        </Callout>
      </DataIndicator>

      {{! Employer Contributions Summary }}
      <DataIndicator
        @source={{this.contributionsSource}}
        @showBadge={{true}}
        @dataSourceId="slr-contributions"
        class="mt-6"
      >
        <div class="tio-bg-surface rounded-lg p-6 tio-hairline-card">
          <div class="tio-row-between mb-4">
            <div class="tio-row-tight">
              <PhTrendUp @size={{24}} class="tio-fg-success" />
              <Heading @level={{2}} @size="lg">
                {{this.employerName}}
                Contributions
              </Heading>
            </div>
            <LinkTo
              @route="authenticated.reimagined.finance.history"
              class="tio-inline-row gap-1 tio-text-sm-medium tio-fg-brand hover:tio-fg-brand"
            >
              <span>View Full History</span>
              <PhArrowRight @size={{16}} />
            </LinkTo>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="text-center p-4 tio-surface-sunken">
              <div class="tio-text-3xl-bold tio-fg-success">
                {{formatCurrency this.contributions.yearToDate}}
              </div>
              <Text @variant="muted">This Year</Text>
            </div>
            <div class="text-center p-4 tio-surface-sunken">
              <div class="tio-text-3xl-bold tio-fg-brand">{{formatCurrency
                  this.contributions.lifetime
                }}</div>
              <Text @variant="muted">Lifetime Total</Text>
            </div>
            <div class="text-center p-4 tio-surface-sunken">
              <div class="tio-text-3xl-bold tio-fg-info">
                {{formatCurrency this.contributions.projectedAnnual}}
              </div>
              <Text @variant="muted">Projected This Year</Text>
            </div>
          </div>
        </div>
      </DataIndicator>
    </VStack>
  </template>
}
